import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import { FiPlus, FiEdit2, FiTrash2, FiCheck } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const PRESET_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e",
  "#3b82f6", "#6366f1", "#8b5cf6", "#ec4899",
  "#14b8a6", "#64748b",
];

const tableColumns = [
  { name: "", id: 0, sortable: false },
  { name: "Ad", dataType: "text", id: 1, customSorter: (a, b) => String(a?.name || "").localeCompare(String(b?.name || "")) },
  { name: "Duruş Grubu", id: 2, sortable: false },
  { name: "OEE Grubu", id: 3, sortable: false },
  { name: "OEE Sınıfı", id: 4, sortable: false },
  { name: "Açıklama", id: 5, sortable: false },
  { name: "Durum", dataType: "text", id: 6, customSorter: (a, b) => Number(!!b?.isActive) - Number(!!a?.isActive) },
  { name: "İşlemler", id: 7, sortable: false },
];

const emptyForm = () => ({
  name: "",
  description: "",
  stoppingGroup: "",
  color: "#6366f1",
  oeeGroup: "",
  isActive: true,
});

const toastAppearance = (type) => {
  if (type === "error") return "danger";
  return type || "success";
};

const StopReasons = () => {
  useTitle("Rovion MES - Duruşlar");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(currentUser.role, "/stop-reasons", currentUser.pages || [], !!currentUser.fullAccess);

  const [reasons, setReasons] = useState([]);
  const [stoppingGroups, setStoppingGroups] = useState([]);
  const [oeeGroups, setOeeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("stop-reasons-list", tableColumns);

  const dismissToast = useCallback((id) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  const addToast = useCallback((message, type = "success") => {
    setToasts((prev) => [...prev, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken]
  );

  const fetchReasons = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/stop-reasons`, authHeaders());
      setReasons(data.stopReasons || []);
    } catch {
      addToast("Duruşlar yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast]);

  const fetchStoppingGroups = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/stopping-groups`, authHeaders());
      setStoppingGroups(data.stoppingGroups || []);
    } catch { }
  }, [authHeaders]);

  const fetchOeeGroups = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/oee-groups`, authHeaders());
      setOeeGroups(data.oeeGroups || []);
    } catch { }
  }, [authHeaders]);

  useEffect(() => {
    fetchReasons();
    fetchStoppingGroups();
    fetchOeeGroups();
  }, [fetchReasons, fetchStoppingGroups, fetchOeeGroups]);

  const openCreate = () => { setFormData(emptyForm()); setEditingItem(null); setSidebarOpened(true); };

  const openEdit = (item) => {
    setFormData({
      name: item.name || "",
      description: item.description || "",
      stoppingGroup: item.stoppingGroup?._id || item.stoppingGroup || "",
      color: item.color || "#6366f1",
      oeeGroup: item.oeeGroup?._id || item.oeeGroup || "",
      isActive: item.isActive !== false,
    });
    setEditingItem(item);
    setSidebarOpened(true);
  };

  const closeSidebar = () => { setSidebarOpened(false); setEditingItem(null); setFormData(emptyForm()); };

  const handleSave = async () => {
    if (!formData.name.trim()) { addToast("Ad zorunludur", "danger"); return; }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        stoppingGroup: formData.stoppingGroup || null,
        color: formData.color || "#6366f1",
        oeeGroup: formData.oeeGroup || null,
        isActive: formData.isActive,
      };
      if (editingItem) {
        await axios.put(`${API_URL}/api/stop-reasons/${editingItem._id}`, payload, authHeaders());
        addToast("Güncellendi", "success");
      } else {
        await axios.post(`${API_URL}/api/stop-reasons`, payload, authHeaders());
        addToast("Oluşturuldu", "success");
      }
      closeSidebar();
      fetchReasons();
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/stop-reasons/${id}`, authHeaders());
      addToast("Silindi", "success");
      fetchReasons();
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  const selectStyle = { background: "var(--m-card-background-color, #fff)", border: "1px solid #e2e5e8", borderRadius: "6px" };
  const selectInner = { background: "transparent", border: "none", outline: "none", width: "100%", padding: "8px 12px", fontSize: "14px", color: "inherit", cursor: "pointer" };

  return (
    <AppPage
      title="Duruşlar"
      actions={canWrite && (
        <Button appearance="primary-animated" onClick={openCreate} icon={<FiPlus />}>
          Yeni Duruş
        </Button>
      )}
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} title={t.message} appearance={toastAppearance(t.type)} onClose={() => dismissToast(t.id)} />
        ))}
      </ToastContainer>

      <Modal isOpen={confirmModal.isOpen} onClose={() => setConfirmModal({ isOpen: false, id: null })} width="small">
        <ModalHeader>Duruşu Sil</ModalHeader>
        <ModalBody><p className="sub-title-page">Bu duruşu silmek istediğinizden emin misiniz?</p></ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button appearance="danger" onClick={() => handleDelete(confirmModal.id)}>Sil</Button>
            <Button appearance="subtle" onClick={() => setConfirmModal({ isOpen: false, id: null })}>İptal</Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <CardContainer>
        <Table
          columns={tableColumns}
          visibleColumns={visibleColumns}
          loading={loading}
          search={true}
          viewOptions={
            <ListViewOptionsPanel
              columns={tableColumns}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumnVisibility}
              onResetColumns={resetToDefault}
              showApply={false}
              showReset={false}
            />
          }
        >
          {reasons.map((r) => (
            <Tr key={r._id} data={r}>
              {visibleColumns.includes(0) && (
                <Td>
                  <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: "50%", background: r.color || "#6366f1", border: "1px solid rgba(0,0,0,0.1)", flexShrink: 0 }} />
                </Td>
              )}
              {visibleColumns.includes(1) && (
                <Td>
                  <strong style={{ fontSize: "0.875rem" }}>{r.name}</strong>
                </Td>
              )}
              {visibleColumns.includes(2) && (
                <Td>
                  {r.stoppingGroup ? (
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: r.stoppingGroup.color || "#6366f1", flexShrink: 0 }} />
                      <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>{r.stoppingGroup.name}</span>
                    </div>
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </Td>
              )}
              {visibleColumns.includes(3) && (
                <Td>
                  {r.oeeGroup ? (
                    <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>{r.oeeGroup?.name || "—"}</span>
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </Td>
              )}
              {visibleColumns.includes(4) && (
                <Td>
                  {r.oeeGroup?.oeeClass ? (
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: r.oeeGroup.oeeClass.color || "#6366f1", flexShrink: 0 }} />
                      <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#475569" }}>{r.oeeGroup.oeeClass.name}</span>
                    </div>
                  ) : (
                    <span className="text-muted small">—</span>
                  )}
                </Td>
              )}
              {visibleColumns.includes(5) && (
                <Td><span className="text-muted small">{r.description || "—"}</span></Td>
              )}
              {visibleColumns.includes(6) && (
                <Td>
                  {r.isActive ? <Lozenge appearance="success-subtle" isBold>Aktif</Lozenge> : <Lozenge appearance="default-subtle" isBold>Pasif</Lozenge>}
                </Td>
              )}
              {visibleColumns.includes(7) && (
                <Td>
                  {canWrite && (
                    <div className="d-flex gap-2">
                      <IconButton icon={<FiEdit2 />} label="Düzenle" appearance="subtle" shape="circle" onClick={() => openEdit(r)} />
                      <IconButton icon={<FiTrash2 />} label="Sil" appearance="subtle" shape="circle" onClick={() => setConfirmModal({ isOpen: true, id: r._id })} />
                    </div>
                  )}
                </Td>
              )}
            </Tr>
          ))}
        </Table>
      </CardContainer>

      <SidebarContainer show={sidebarOpened} title={editingItem ? "Duruş Düzenle" : "Yeni Duruş"} onClose={closeSidebar}>
        <div className="d-flex flex-column" style={{ height: "100%" }}>
          <div className="d-flex flex-column gap-3">
            <div>
              <Label required>Ad</Label>
              <Input value={formData.name} onChange={(v) => setFormData((p) => ({ ...p, name: v }))} placeholder="örn. Elektrik arızası" />
            </div>

            <div>
              <Label>Duruş Grubu</Label>
              <div style={selectStyle}>
                <select style={selectInner} value={formData.stoppingGroup} onChange={(e) => setFormData((p) => ({ ...p, stoppingGroup: e.target.value }))}>
                  <option value="">— Seçin —</option>
                  {stoppingGroups.map((g) => (
                    <option key={g._id} value={g._id}>{g.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>OEE Grubu</Label>
              <div style={selectStyle}>
                <select style={selectInner} value={formData.oeeGroup} onChange={(e) => setFormData((p) => ({ ...p, oeeGroup: e.target.value }))}>
                  <option value="">— Seçin —</option>
                  {oeeGroups.map((g) => (
                    <option key={g._id} value={g._id}>{g.name}{g.oeeClass ? ` (${g.oeeClass.name})` : ""}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Açıklama</Label>
              <Input value={formData.description} onChange={(v) => setFormData((p) => ({ ...p, description: v }))} placeholder="Kısa açıklama (isteğe bağlı)" />
            </div>

            <div>
              <Label>Renk</Label>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {PRESET_COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setFormData((p) => ({ ...p, color: c }))}
                    style={{ width: 28, height: 28, borderRadius: "50%", background: c, border: "2px solid rgba(0,0,0,0.08)", cursor: "pointer", outline: "none", boxShadow: formData.color === c ? "0 0 0 3px #111827" : "none", transition: "box-shadow 0.15s, transform 0.15s", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", transform: formData.color === c ? "scale(1.05)" : "none" }}>
                    {formData.color === c && <FiCheck size={14} style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.35))" }} />}
                  </button>
                ))}
                <label style={{ width: 28, height: 28, borderRadius: "50%", border: "2px dashed #cbd5e1", background: !PRESET_COLORS.includes(formData.color) && formData.color ? formData.color : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative", overflow: "hidden", fontSize: 16, color: !PRESET_COLORS.includes(formData.color) && formData.color ? "#fff" : "#64748b", flexShrink: 0, boxShadow: !PRESET_COLORS.includes(formData.color) && formData.color ? "0 0 0 3px #111827" : "none" }}>
                  <input type="color" value={formData.color} onChange={(e) => setFormData((p) => ({ ...p, color: e.target.value }))} style={{ position: "absolute", width: "200%", height: "200%", top: "-50%", left: "-50%", opacity: 0, cursor: "pointer" }} />
                  {!PRESET_COLORS.includes(formData.color) ? <FiCheck size={14} /> : "+"}
                </label>
              </div>
            </div>
          </div>

          <div className="mt-auto pt-3">
            <Button appearance="primary-animated" onClick={handleSave} disabled={saving} icon={editingItem ? <FiEdit2 /> : <FiPlus />} className="w-100">
              {saving ? "Kaydediliyor..." : editingItem ? "Güncelle" : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>
    </AppPage>
  );
};

export default withAuth(StopReasons);
